import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { PrismaEnums } from '@airreading/schema/browser';
import { CSVUploadResponse } from '../csv.js';
import { classTimeTypeSchema } from './class-time-shared.js';
import { fullClassTimeSchema } from './class-time.js';
import { learningPathWithLessonsSchema } from './learning-path.js';
import { lessonSchema } from './lesson.js';
import { userLikeSchema, userWithCoachProfileSchema } from './user.js';
import { paginatedResponse } from './util-schema-types.js';

import { filteringSchemaFrom, pagingSchema, sortingSchemaFrom, sortOrderSchemaFrom } from './modifier.js';
import { coachSessionRemarkSchema, coachSessionRemarkV2Schema, sessionStatusSchema } from './session-shared.js';
import {
  AttendanceStatus,
  AttendanceStatusLabelMap,
  attendanceStatusSchema,
  AttendanceStatusSelectable,
  attendanceStatusSelectableSchema,
  attendanceStatusSelectableValues,
  CoachAttendanceLateTagRegex,
  CoachAttendanceRollupItem,
  coachAttendanceRollupItemSchema,
  CoachAttendanceStatus,
  CoachAttendanceStatusLabelMap,
  coachAttendanceStatusSchema,
  CoachAttendanceTag,
  CoachAttendanceTagCategoryMap,
  CoachAttendanceTagLabelMap,
  coachAttendanceTagSchema,
  EmailsSent,
  EmailsSentLabelMap,
  emailsSentSchema,
  GetCoachAttendanceRollupResult,
  getCoachAttendanceRollupResultSchema,
  StudentGroup,
  studentGroupSchema,
  StudentGroupStatus,
  StudentGroupStatusLabelMap,
  studentGroupStatusSchema,
  studentGroupWithClassTimeSchema,
} from './student-group-shared.js';
export {
  AttendanceStatus,
  AttendanceStatusLabelMap,
  attendanceStatusSchema,
  attendanceStatusSelectableSchema,
  attendanceStatusSelectableValues,
  CoachAttendanceLateTagRegex,
  coachAttendanceRollupItemSchema,
  CoachAttendanceStatus,
  CoachAttendanceStatusLabelMap,
  coachAttendanceStatusSchema,
  CoachAttendanceTagCategoryMap,
  CoachAttendanceTagLabelMap,
  coachAttendanceTagSchema,
  EmailsSent,
  EmailsSentLabelMap,
  emailsSentSchema,
  getCoachAttendanceRollupResultSchema,
  studentGroupSchema,
  StudentGroupStatus,
  StudentGroupStatusLabelMap,
  studentGroupStatusSchema,
  studentGroupWithClassTimeSchema,
};
export type {
  AttendanceStatusSelectable,
  CoachAttendanceRollupItem,
  CoachAttendanceTag,
  GetCoachAttendanceRollupResult,
  StudentGroup,
};

export const studentGroupWithNamesSchema = studentGroupSchema.extend({
  studentNames: z.array(z.string()).optional(),
});
export type StudentGroupWithNames = z.infer<typeof studentGroupWithNamesSchema>;

export const studentGroupWithScheduleAndLevelSchema = studentGroupSchema.extend({
  coach: z.lazy(() => userWithCoachProfileSchema.nullable()),
  students: z.array(z.lazy(() => userLikeSchema)),
  classTime: z.lazy(() => fullClassTimeSchema),
  learningPath: z.lazy(() => learningPathWithLessonsSchema),
  startingLesson: z.lazy(() => lessonSchema),
  nextLesson: z
    .lazy(() => lessonSchema)
    .nullable()
    .optional(),
  nextSessionAtInstant: z.instanceof(Temporal.Instant).nullable().optional(),
  grades: z.array(z.string()),
  futureSessions: z.number().int().optional(),
  sessionCount: z.number().int(),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  coachAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  coachLastAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  coachLastUnassignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  studentCount: z.number().int(),
  maxPlannedAbsences: z.number().int(),
  oneTimeConflictCount: z.number().int().optional(),
  coachAttendanceRollup: getCoachAttendanceRollupResultSchema.optional(),
  attendanceStatus: attendanceStatusSchema.nullable(),
  emailsSent: emailsSentSchema.array(),
  attendanceNotes: z.string().nullable(),
  v1AbsenceText: z.string().nullable().optional(),
});

export type StudentGroupWithScheduleAndLevel = z.infer<typeof studentGroupWithScheduleAndLevelSchema>;

export const studentGroupsWithScheduleAndLevelSchema = paginatedResponse.extend({
  studentGroups: z.array(studentGroupWithScheduleAndLevelSchema),
});

export type StudentGroupsWithScheduleAndLevel = z.infer<typeof studentGroupsWithScheduleAndLevelSchema>;

export const getStudentGroupsFilterSchema = z.object({
  query: z.string().optional(),
  scheduleSchools: z.array(z.string()).optional(),
  statuses: z.array(studentGroupStatusSchema).optional(),
  learningPaths: z.array(z.string()).optional(),
  startingLessons: z.array(z.string()).optional(),
  classTimeIds: z.array(z.string()).optional(),
  classTimeTypes: z.array(classTimeTypeSchema).optional(),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
});
export type GetStudentGroupsFilter = z.infer<typeof getStudentGroupsFilterSchema>;

export const getStudentGroupsSortableColumnSchema = studentGroupWithScheduleAndLevelSchema
  .keyof()
  .extract(['learningPath', 'startingLesson']);
export type GetStudentGroupsSortableColumn = z.infer<typeof getStudentGroupsSortableColumnSchema>;

export const getStudentGroupsSortOrderSchema = sortOrderSchemaFrom(getStudentGroupsSortableColumnSchema);
export type GetStudentGroupsSortOrder = z.infer<typeof getStudentGroupsSortOrderSchema>;

export const getStudentGroupsModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getStudentGroupsFilterSchema))
  .merge(sortingSchemaFrom(getStudentGroupsSortableColumnSchema));
export type GetStudentGroupsModifier = z.infer<typeof getStudentGroupsModifierSchema>;

export const createStudentGroupOptionsSchema = z.object({
  coachId: z.string().optional(),
  classTimeId: z.string(),
  learningPathId: z.string(),
  startingLessonId: z.string(),
  studentIds: z.array(z.string()),
  status: z.nativeEnum(PrismaEnums.StudentGroupStatus),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  v1CoachAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  v1AbsenceText: z.string().nullable().optional(),
});
export type CreateStudentGroupOptions = z.infer<typeof createStudentGroupOptionsSchema>;

export type CreateStudentGroupsCsvResult = CSVUploadResponse;

export const updateStudentGroupOptionsSchema = z.object({
  coachId: z.string().optional(),
  classTimeId: z.string(),
  learningPathId: z.string(),
  startingLessonId: z.string(),
  studentIds: z.array(z.string()),
  status: z.nativeEnum(PrismaEnums.StudentGroupStatus),
  overrideStartPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  overrideEndPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  v1CoachAssignedAtPlainDate: z.instanceof(Temporal.PlainDate).optional(),
  attendanceStatus: attendanceStatusSchema.nullable(),
  emailsSent: emailsSentSchema.array(),
  coachUnassignedAtPlainDate: z.instanceof(Temporal.PlainDate).nullable().optional(),
  attendanceNotes: z.string().nullable(),
  v1AbsenceText: z.string().nullable().optional(),
});
export type UpdateStudentGroupOptions = z.infer<typeof updateStudentGroupOptionsSchema>;

export const createStudentGroupCSVRowSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email'),
  learningPathName: z.string().min(1, 'Learning path name is required'),
  startingLessonName: z.string().min(1, 'Starting lesson name is required'),
  classTimeId: z.string().min(1, 'Class time ID is required'),
});

export type CreateStudentGroupCSVRow = z.infer<typeof createStudentGroupCSVRowSchema>;
export const CreateStudentGroupsCSVFormKeys = {
  studentGroupsCSV: 'studentGroupsCSV',
  studentsPerGroup: 'studentsPerGroup',
  schoolId: 'schoolId',
} as const;

export const createStudentGroupsCsvOptionsSchema = z.object({
  [CreateStudentGroupsCSVFormKeys.studentsPerGroup]: z.coerce
    .number()
    .positive({ message: 'Students per group must be positive' })
    .int({ message: 'Students per group must be an integer' }),
  [CreateStudentGroupsCSVFormKeys.schoolId]: z.string({ message: 'School is required' }),
});

export type CreateStudentGroupsCsvOptions = z.infer<typeof createStudentGroupsCsvOptionsSchema>;

// One session line for recreate summary (pre-confirm or post) – canceled sessions
export const recreateSessionSummaryItemSchema = z.object({
  startInstant: z.string(),
  endInstant: z.string(),
  timeZone: z.string(),
  teacherName: z.string().nullable(),
  /** Present when a sub was assigned; null = pending sub teacher */
  subTeacherName: z.string().nullable(),
  /** When true, subTeacherName is the previous sub and they are unavailable at the new time */
  previousSubUnavailable: z.boolean().optional(),
});
export type RecreateSessionSummaryItem = z.infer<typeof recreateSessionSummaryItemSchema>;

// Sub session time changed (API shape with string instants) for pre-confirm summary
export const recreateSubSessionTimeChangedSummarySchema = recreateSessionSummaryItemSchema.extend({
  newStartInstant: z.string(),
  newEndInstant: z.string(),
});
export type RecreateSubSessionTimeChangedSummary = z.infer<typeof recreateSubSessionTimeChangedSummarySchema>;

// Pre-confirm summary for Recreate Sessions modal (one modal: no time change vs time change variant)
export const recreateSessionsSummarySchema = z.object({
  classCount: z.number(),
  canceledCount: z.number(),
  subbedCount: z.number(),
  canceledSessions: z.array(recreateSessionSummaryItemSchema),
  subSessions: z.array(recreateSessionSummaryItemSchema),
  subSessionsTimeChangedCount: z.number().optional(),
  subSessionsTimeChanged: z.array(recreateSubSessionTimeChangedSummarySchema).optional(),
});
export type RecreateSessionsSummary = z.infer<typeof recreateSessionsSummarySchema>;

// Metadata for an archived session used during Recreate Sessions
export const recreateArchivedSessionMetaSchema = z.object({
  status: sessionStatusSchema,
  substituteCoachId: z.string().nullable(),
  canceledAtInstant: z.instanceof(Temporal.Instant).nullable(),
  hoursCanceledBeforeStart: z.number().nullable(),
  coachSessionRemarks: z.array(coachSessionRemarkSchema),
  coachSessionRemarksV2: z.array(coachSessionRemarkV2Schema),
  substitutionRequestIds: z.array(z.string()),
  startInstant: z.instanceof(Temporal.Instant),
  endInstant: z.instanceof(Temporal.Instant),
  timeZone: z.string(),
  teacherName: z.string().nullable(),
  subTeacherName: z.string().nullable(),
});
export type RecreateArchivedSessionMeta = z.infer<typeof recreateArchivedSessionMetaSchema>;

export const updateStudentGroupBulkActionsResultsSchema = z.object({
  errors: z.array(z.string()).optional(),
  success: z.number(),
});
export type UpdateStudentGroupBulkActionsResults = z.infer<typeof updateStudentGroupBulkActionsResultsSchema>;

export const scheduleFromStudentGroupBulkActionsSchema = z.enum(['FromNow', 'WithRange']);
export type ScheduleFromStudentGroupBulkActions = z.infer<typeof scheduleFromStudentGroupBulkActionsSchema>;
export const ScheduleFromStudentGroupBulkActions = scheduleFromStudentGroupBulkActionsSchema.enum;
export const ScheduleFromStudentGroupBulkActionsLabelMap: Record<ScheduleFromStudentGroupBulkActions, string> = {
  [ScheduleFromStudentGroupBulkActions.FromNow]: 'Future sessions from now',
  [ScheduleFromStudentGroupBulkActions.WithRange]: 'Future sessions in range',
};

export const editStudentGroupBulkActionsSchema = z.enum([
  'ExtendLastSession',
  'UpdateTeacher',
  'UpdateLesson',
  'UpdateStudents',
  'RemoveFutureSessions',
  'RecreateSessions',
  'ArchiveSessions',
  'MergeClasses',
  'CancelOneTimeClassFutureSessions',
  'RecreateSessionsForOneTimeClass',
]);
export type EditStudentGroupBulkActions = z.infer<typeof editStudentGroupBulkActionsSchema>;
export const EditStudentGroupBulkActions = editStudentGroupBulkActionsSchema.enum;

export const baseEditStudentGroupsBulkActionsOptionsSchema = z.object({
  studentGroupIds: z.array(z.string()),
  startPlainDate: z.instanceof(Temporal.PlainDate),
  endPlainDate: z.instanceof(Temporal.PlainDate),
  timeZone: z.string(),
});

export const editBulkActionExtendLastSessionSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.ExtendLastSession),
});
export type EditBulkActionExtendLastSession = z.infer<typeof editBulkActionExtendLastSessionSchema>;

export const editBulkActionRemoveFutureSessionsSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.RemoveFutureSessions),
});
export type EditBulkActionRemoveFutureSessions = z.infer<typeof editBulkActionRemoveFutureSessionsSchema>;

export const editBulkActionUpdateTeacherSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.UpdateTeacher),
  coachId: z.string(),
  reassigned: z.boolean().optional(),
});
export type EditBulkActionUpdateTeacher = z.infer<typeof editBulkActionUpdateTeacherSchema>;

export const editBulkActionUpdateLessonSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.UpdateLesson),
  learningPathId: z.string(),
  startingLessonId: z.string(),
});
export type EditBulkActionUpdateLesson = z.infer<typeof editBulkActionUpdateLessonSchema>;

export const editBulkActionUpdateStudentsSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.UpdateStudents),
  studentIds: z.array(z.string()),
});
export type EditBulkActionUpdateStudents = z.infer<typeof editBulkActionUpdateStudentsSchema>;

export const editBulkActionRecreateSessionsSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.RecreateSessions),
  coachId: z.string().optional(),
  studentIds: z.array(z.string()).optional(),
  learningPathId: z.string().optional(),
  startingLessonId: z.string().optional(),
  scheduleId: z.string().optional(),
  classTimeId: z.string().optional(),
});
export type EditBulkActionRecreateSessions = z.infer<typeof editBulkActionRecreateSessionsSchema>;

export const editBulkActionArchiveSessionsSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.ArchiveSessions),
  status: studentGroupStatusSchema,
});
export type EditBulkActionArchiveSessions = z.infer<typeof editBulkActionArchiveSessionsSchema>;

export const editBulkActionMergeClassesSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend({
  bulkType: z.literal(EditStudentGroupBulkActions.MergeClasses),
  coachId: z.string().optional(),
  studentIds: z.array(z.string()).optional(),
  learningPathId: z.string().optional(),
  startingLessonId: z.string().optional(),
});
export type EditBulkActionMergeClasses = z.infer<typeof editBulkActionMergeClassesSchema>;

export const editBulkActionCancelOneTimeClassFutureSessionsSchema =
  baseEditStudentGroupsBulkActionsOptionsSchema.extend({
    bulkType: z.literal(EditStudentGroupBulkActions.CancelOneTimeClassFutureSessions),
  });
export type EditBulkActionCancelOneTimeClassFutureSessions = z.infer<
  typeof editBulkActionCancelOneTimeClassFutureSessionsSchema
>;

export const editBulkActionRecreateSessionsForOneTimeClassSchema = baseEditStudentGroupsBulkActionsOptionsSchema.extend(
  {
    bulkType: z.literal(EditStudentGroupBulkActions.RecreateSessionsForOneTimeClass),
  },
);
export type EditBulkActionRecreateSessionsForOneTimeClass = z.infer<
  typeof editBulkActionRecreateSessionsForOneTimeClassSchema
>;

export const editStudentGroupsBulkActionsOptionsSchema = z.discriminatedUnion('bulkType', [
  editBulkActionExtendLastSessionSchema,
  editBulkActionRemoveFutureSessionsSchema,
  editBulkActionUpdateTeacherSchema,
  editBulkActionUpdateLessonSchema,
  editBulkActionUpdateStudentsSchema,
  editBulkActionRecreateSessionsSchema,
  editBulkActionArchiveSessionsSchema,
  editBulkActionMergeClassesSchema,
  editBulkActionCancelOneTimeClassFutureSessionsSchema,
  editBulkActionRecreateSessionsForOneTimeClassSchema,
]);
export type EditStudentGroupsBulkActionsOptions = z.infer<typeof editStudentGroupsBulkActionsOptionsSchema>;

export const editSessionsStudentGroupsBulkActionsSchema = z.object({
  coachId: z.string().optional(),
  studentIds: z.array(z.string()).optional(),
  learningPathId: z.string().optional(),
  startingLessonId: z.string().optional(),
  scheduleId: z.string().optional(),
  classTimeId: z.string().optional(),
  reassigned: z.boolean().optional(),
  timeZone: z.string().optional(),
});
export type EditSessionsStudentGroupsBulkActions = z.infer<typeof editSessionsStudentGroupsBulkActionsSchema>;

export const coachAttendanceSessionsRollupSchema = z.object({
  coachId: z.string(),
  studentGroupIds: z.array(z.string()),
});
export type CoachAttendanceSessionsRollup = z.infer<typeof coachAttendanceSessionsRollupSchema>;

export const getCoachAttendanceRollupOptionsSchema = z.object({
  coachId: z.string(),
  studentGroup: studentGroupWithScheduleAndLevelSchema,
  startOnOrAfter: z.string().optional(),
  endOnOrBefore: z.string().optional(),
});
export type GetCoachAttendanceRollupOptions = z.infer<typeof getCoachAttendanceRollupOptionsSchema>;

export const getStudentGroupsAttendanceSortableColumnSchema = studentGroupWithScheduleAndLevelSchema
  .keyof()
  .extract(['coachAssignedAtPlainDate']);
export type GetStudentGroupsAttendanceSortableColumn = z.infer<typeof getStudentGroupsAttendanceSortableColumnSchema>;

export const getStudentGroupsAttendanceSortOrderSchema = sortOrderSchemaFrom(
  getStudentGroupsAttendanceSortableColumnSchema,
);
export type GetStudentGroupsAttendanceSortOrder = z.infer<typeof getStudentGroupsAttendanceSortOrderSchema>;

export const getStudentGroupsAttendanceFilterSchema = z.object({
  query: z.string().optional(),
  issueDate: z.string().optional(),
  attendanceStatus: z.array(attendanceStatusSchema).optional(),
  reassignmentDate: z.string().optional(),
});
export type GetStudentGroupsAttendanceFilter = z.infer<typeof getStudentGroupsAttendanceFilterSchema>;

export const getStudentGroupsAttendanceModifierSchema = pagingSchema
  .merge(filteringSchemaFrom(getStudentGroupsAttendanceFilterSchema))
  .merge(sortingSchemaFrom(getStudentGroupsAttendanceSortableColumnSchema));
export type GetStudentGroupsAttendanceModifier = z.infer<typeof getStudentGroupsAttendanceModifierSchema>;

export const getStudentGroupsAttendanceResultsSchema = paginatedResponse.extend({
  studentGroups: z.array(studentGroupWithScheduleAndLevelSchema),
});
export type GetStudentGroupsAttendanceResults = z.infer<typeof getStudentGroupsAttendanceResultsSchema>;
